import { useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogCancel } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Alert } from '@/components/ui/Alert';
import { ApiErrorAlert } from '@/components/ui/ApiErrorAlert';
import { Spinner } from '@/components/ui/Feedback';
import { listDeviceGroups, listOrganizations, listSites } from '@/lib/api/endpoints/organizations';
import { moveDevice } from '@/lib/api/endpoints/devices';
import { queryKeys } from '@/lib/query-keys';
import { isApiError } from '@/lib/api/errors';
import {
  describePlacement,
  groupsForPlacement,
  isPlacementConsistent,
  sitesForOrganization,
} from '@/features/organizations/hierarchy';
import type { Device, DevicePlacementUpdate } from '@/lib/api/types';

/*
  Move a device to another organization, site or group.

  The three pickers are dependent, and the dependency is enforced by what the later ones
  offer rather than by validating afterwards:

  * Changing the organization clears the site and the group, because neither can survive the
    move. Offering a stale site would let the technician submit a device whose site belongs to
    a different customer.
  * The site list is the selected organization's sites only.
  * The group list is organization wide groups plus groups at the selected site, per
    groupsForPlacement in ../organizations/hierarchy.ts.

  Moving across organizations is called out in the dialog rather than done quietly. It is the
  one change here that alters who can reach the machine: every role assignment is scoped to a
  node in the hierarchy, so a device that leaves an organization leaves the reach of everyone
  scoped to it and enters the reach of everyone scoped to the destination. That is not
  something to discover afterwards from the audit log.
*/

export interface MoveDeviceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly device: Device;
  readonly currentOrganizationName: string;
}

const NO_PARENT = '__none__';

export function MoveDeviceDialog({
  open,
  onOpenChange,
  device,
  currentOrganizationName,
}: MoveDeviceDialogProps): React.JSX.Element {
  const formId = `move-device-${useId()}`;
  const queryClient = useQueryClient();

  const [organizationId, setOrganizationId] = useState(device.organizationId);
  const [siteId, setSiteId] = useState<string>(device.siteId ?? NO_PARENT);
  const [deviceGroupId, setDeviceGroupId] = useState<string>(device.deviceGroupId ?? NO_PARENT);

  const organizations = useQuery({
    queryKey: queryKeys.organizations,
    queryFn: listOrganizations,
    staleTime: 5 * 60_000,
  });
  const sites = useQuery({ queryKey: queryKeys.sites(), queryFn: () => listSites() });
  const groups = useQuery({
    queryKey: queryKeys.deviceGroups(),
    queryFn: () => listDeviceGroups(),
  });

  // Reopening the dialog starts from the device's current placement again, not from whatever
  // the technician half-selected and then cancelled. Adjusted during render rather than in an
  // effect, for the reason spelled out in ConfirmDialog.
  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setOrganizationId(device.organizationId);
      setSiteId(device.siteId ?? NO_PARENT);
      setDeviceGroupId(device.deviceGroupId ?? NO_PARENT);
    }
  }

  // Memoised because these feed useMemo dependency lists below, and `data ?? []` produces a
  // fresh array identity on every render, which would defeat the memo it is a dependency of.
  const allSites = useMemo(() => sites.data ?? [], [sites.data]);
  const allGroups = useMemo(() => groups.data ?? [], [groups.data]);
  const allOrganizations = organizations.data ?? [];

  const siteOptions = useMemo(
    () => sitesForOrganization(allSites, organizationId),
    [allSites, organizationId],
  );
  const groupOptions = useMemo(
    () => groupsForPlacement(allGroups, organizationId, siteId === NO_PARENT ? null : siteId),
    [allGroups, organizationId, siteId],
  );

  const placement: DevicePlacementUpdate = {
    organizationId,
    siteId: siteId === NO_PARENT ? null : siteId,
    deviceGroupId: deviceGroupId === NO_PARENT ? null : deviceGroupId,
  };

  const mutation = useMutation({
    mutationFn: (body: DevicePlacementUpdate) => moveDevice(device.id, body),
    onSuccess: () => {
      // One invalidation covers the list and every detail view, because the detail key is
      // nested under 'devices'. See src/lib/query-keys.ts.
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
      onOpenChange(false);
    },
  });

  const loading = organizations.isPending || sites.isPending || groups.isPending;
  const crossOrganization = organizationId !== device.organizationId;
  const unchanged =
    placement.organizationId === device.organizationId &&
    placement.siteId === device.siteId &&
    placement.deviceGroupId === device.deviceGroupId;
  const consistent = isPlacementConsistent(allSites, allGroups, placement);

  const fieldErrors = isApiError(mutation.error) ? mutation.error.fieldErrors : {};

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (loading || unchanged || !consistent || mutation.isPending) {
      return;
    }
    mutation.mutate(placement);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Move ${device.hostname}`}
      description="Change which customer, site and group this device belongs to. Requires device.manage."
      busy={mutation.isPending}
      footer={
        <>
          <DialogCancel disabled={mutation.isPending} />
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={mutation.isPending}
            disabled={loading || unchanged || !consistent}
          >
            Move device
          </Button>
        </>
      }
    >
      {loading ? (
        <Spinner label="Loading the organization hierarchy" />
      ) : (
        <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Select
            label="Organization"
            value={organizationId}
            options={allOrganizations.map((org) => ({
              value: org.id,
              label: org.status === 'active' ? org.name : `${org.name} (${org.status})`,
            }))}
            onValueChange={(next) => {
              setOrganizationId(next);
              // A site and a group from the old organization cannot survive the move, so
              // they are cleared rather than left to fail validation.
              setSiteId(NO_PARENT);
              setDeviceGroupId(NO_PARENT);
            }}
          />

          <Select
            label="Site"
            value={siteId}
            options={[
              { value: NO_PARENT, label: 'No site (organization level)' },
              ...siteOptions.map((site) => ({ value: site.id, label: site.name })),
            ]}
            onValueChange={(next) => {
              setSiteId(next);
              // A site group from the previous site is not valid at the new one.
              const stillValid = groupsForPlacement(
                allGroups,
                organizationId,
                next === NO_PARENT ? null : next,
              ).some((group) => group.id === deviceGroupId);
              if (!stillValid) {
                setDeviceGroupId(NO_PARENT);
              }
            }}
          />

          <Select
            label="Device group"
            value={deviceGroupId}
            options={[
              { value: NO_PARENT, label: 'No group' },
              ...groupOptions.map((group) => ({
                value: group.id,
                label: group.siteId === null ? `${group.name} (organization wide)` : group.name,
              })),
            ]}
            onValueChange={setDeviceGroupId}
          />

          <p className="text-xs text-fg-muted">
            New placement:{' '}
            <span className="font-medium text-fg">
              {describePlacement(allOrganizations, allSites, allGroups, placement)}
            </span>
          </p>

          {crossOrganization ? (
            <Alert tone="warning" title="This moves the device to a different customer">
              It leaves {currentOrganizationName}. Everyone whose role is scoped to{' '}
              {currentOrganizationName} loses access to it, and everyone scoped to the
              destination gains access. Assignments scoped to this device by id are unaffected.
            </Alert>
          ) : null}

          {fieldErrors.siteId?.[0] !== undefined ? (
            <Alert tone="danger" title="Site rejected" liveness="assertive">
              {fieldErrors.siteId[0]}
            </Alert>
          ) : null}
          {fieldErrors.deviceGroupId?.[0] !== undefined ? (
            <Alert tone="danger" title="Device group rejected" liveness="assertive">
              {fieldErrors.deviceGroupId[0]}
            </Alert>
          ) : null}

          {mutation.isError &&
          fieldErrors.siteId === undefined &&
          fieldErrors.deviceGroupId === undefined ? (
            <ApiErrorAlert error={mutation.error} context="Could not move the device" />
          ) : null}

          {unchanged ? (
            <p className="text-xs text-fg-subtle">
              Nothing has changed yet, so there is nothing to save.
            </p>
          ) : null}
        </form>
      )}
    </Dialog>
  );
}
